import React, { useState } from 'react';

import { observer } from 'mobx-react-lite';
import { IncomingPhoneNumberType } from 'stores/incoming_phone_number_store';
import useStores from 'hooks/useStores';


type IncomingPhoneNumberContextType = {
    getOnly: (workKey: string, associated_type: string, associated_uuid: string) => Promise<IncomingPhoneNumberType>;
    getOrCreate: (workKey: string, associated_type: string, associated_uuid: string) => Promise<IncomingPhoneNumberType>;

    // Fetch and get list by association
    fetchListByAssociation: (identity: string, associated_type: string, associated_uuid: string) => Promise<IncomingPhoneNumberType[]>;
    getListByAssociation: (associated_type: string, associated_uuid: string) => IncomingPhoneNumberType[];

    // Contains all known IncomingPhoneNumber items
    items: IncomingPhoneNumberType[];
}

export const IncomingPhoneNumberContext = React.createContext<IncomingPhoneNumberContextType>({
    getOnly: () => { throw 'Not Implemented' },
    getOrCreate: () => { throw 'Not Implemented' },
    fetchListByAssociation: () => { throw 'Not Implemented' },
    getListByAssociation: () => { throw 'Not Implemented' },
    items: [] as IncomingPhoneNumberType[],
});



export const IncomingPhoneNumberProvider = observer(({ children }: { children: React.ReactNode }) => {
    const [items, setIncomingPhoneNumber] = useState([] as IncomingPhoneNumberType[]);
    const { incomingPhoneNumberStore } = useStores();

    const fetchIncomingPhoneNumber = async (uuid: string) => {
        // Autogenerated
        return incomingPhoneNumberStore.fetchIncomingPhoneNumber(uuid);
    };

    const getIncomingPhoneNumber = (uuid: string) => {
        // Autogenerated
        return items.find((item) => item.uuid === uuid) || null;
    };

    const fetchListByAssociation = async (identity: string, associated_type: string, associated_uuid: string) => {
        return incomingPhoneNumberStore.fetchListByAssociation(identity, associated_type, associated_uuid)
            .then(() => {
                setIncomingPhoneNumber([...items, ...incomingPhoneNumberStore.items]);
                return getListByAssociation(associated_type, associated_uuid);
            });
    };

    const getListByAssociation = (associated_type: string, associated_uuid: string) => {
        return items.slice().filter((item) => item.associated_type === associated_type && item.associated_uuid === associated_uuid);
    };

    const getOrCreate = (workKey: string, associated_type: string, associated_uuid: string) => {
        return incomingPhoneNumberStore.getOrCreate(workKey, associated_type, associated_uuid);
    };

    const getOnly = (identity: string, associated_type: string, associated_uuid: string) => {
        return incomingPhoneNumberStore.getOnly(identity, associated_type, associated_uuid);
    };


    const data = {
        getOnly,
        getOrCreate,
        fetchIncomingPhoneNumber,
        getIncomingPhoneNumber,
        fetchListByAssociation,
        getListByAssociation,
        items,
    };

    return (
        <IncomingPhoneNumberContext.Provider value={data}>{ children }</IncomingPhoneNumberContext.Provider>
    );

});
